import React, { useMemo } from 'react';
import Layout from '../components/Layout';
import countryList from 'react-select-country-list';

// Hooks personnalisés
import useInscription from '../hooks/useInscription';
import usePaymentCalculations from '../hooks/usePaymentCalculations';
import useInscriptionSubmit from '../hooks/useInscriptionSubmit';

// Import des composants existants
import StepperComponent from '../components/StepperComponent';
import StudentInfoForm from '../components/StudentInfoForm';
import ParentInfoForm from '../components/ParentInfoForm';
import PaymentInfoForm from '../components/PaymentInfoForm';
import NavigationButtons from '../components/NavigationButtons';

// Nouveau composant
import TranchesList from '../components/inscription/TranchesList';

const CreateInscriptionPage = () => {
  // Hook principal pour la gestion des données
  const {
    // States
    currentStep,
    loading,
    setLoading,
    error,
    setError,
    errors,
    formData,
    setFormData,
    documents,
    
    // Données de référence
    filieres,
    niveaux,
    classes,
    tarifs,
    fraisInscription,
    tarifsComplementaires,
    modesPaiement,
    tranchesPaiement,
    
    // Suggestions
    suggestedParents,
    showParentSuggestions,
    setShowParentSuggestions,
    
    // Actions
    handleInputChange,
    handleDocumentUpload,
    handleDocumentRemove,
    handleParentSelect,
    searchParents,
    fetchClasses,
    fetchNiveauxByFiliere,
    validateStep,
    nextStep,
    prevStep,
    
    // Navigation
    navigate
  } = useInscription();

  // Hook pour les calculs de paiement
  const {
    // États de paiement
    montantVerse,
    paiementFraisInscription,
    setPaiementFraisInscription,
    remiseType,
    setRemiseType,
    remiseValue,
    tarifsSup,
    setTarifsSup,
    optionsPaiement,
    setOptionsPaiement,
    tranchesRestantes,
    
    // Calculs
    montantTotal,
    calculerMontantActuel,
    calculerRepartitionPaiement,

    // Actions
    handleMontantVerseChange,
    handleRemiseChange,
    toggleTarifSup
  } = usePaymentCalculations(tarifs, fraisInscription, tranchesPaiement);

  // Hook pour la soumission
  const { handleSubmit } = useInscriptionSubmit(
    formData,
    documents,
    tarifsSup,
    optionsPaiement,
    tranchesPaiement,
    tarifs,
    fraisInscription,
    montantVerse,
    paiementFraisInscription,
    tranchesRestantes,
    validateStep,
    setLoading,
    setError,
    navigate
  );

  // Liste des pays mémorisée
  const countries = useMemo(() => {
    return countryList().getData().map(country => ({
      value: country.label,
      label: country.label
    }));
  }, []);

  // Fonction pour rendre les échéances (version optimisée)
  const renderEcheances = () => (
    <TranchesList
      tranchesPaiement={tranchesPaiement}
      tarifs={tarifs}
      calculerRepartitionPaiement={calculerRepartitionPaiement}
      montantVerse={montantVerse}
    />
  );

  if (loading) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#202654]"></div>
            <span className="ml-3 text-gray-600">Chargement des données...</span>
                </div>
              </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 py-4">
        <div className="max-w-6xl mx-auto">
          <div className="mb-4">
            <h1 className="text-2xl font-bold text-gray-900">Nouvelle Inscription</h1>
            <p className="mt-1 text-sm text-gray-600">
              Remplissez les informations pour créer une nouvelle inscription
            </p>
          </div>

          <StepperComponent currentStep={currentStep} />

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
            <form onSubmit={handleSubmit}>
              <div className="space-y-6">
                {currentStep === 1 && (
                  <StudentInfoForm
                    formData={formData}
                    setFormData={setFormData}
                    errors={errors}
                    countries={countries}
                    filieres={filieres}
                    niveaux={niveaux}
                    classes={classes}
                    fetchNiveauxByFiliere={fetchNiveauxByFiliere}
                    fetchClasses={fetchClasses}
                    handleInputChange={handleInputChange}
                  />
                )}

                {currentStep === 2 && (
                  <ParentInfoForm
                    formData={formData}
                    setFormData={setFormData}
                    errors={errors}
                    suggestedParents={suggestedParents}
                    showParentSuggestions={showParentSuggestions}
                    searchParents={searchParents}
                    handleParentSelect={handleParentSelect}
                  />
                )}

                {currentStep === 3 && (
                  <PaymentInfoForm
                    formData={formData}
                    paiementFraisInscription={paiementFraisInscription}
                    setPaiementFraisInscription={setPaiementFraisInscription}
                    fraisInscription={fraisInscription}
                    montantScolarite={tarifs?.prix || 0}
                    montantTotal={montantTotal}
                    tarifsSup={tarifsSup}
                    optionsPaiement={optionsPaiement}
                    setOptionsPaiement={setOptionsPaiement}
                    remiseType={remiseType}
                    setRemiseType={setRemiseType}
                    remiseValue={remiseValue}
                    handleRemiseChange={handleRemiseChange}
                    tarifsComplementaires={tarifsComplementaires}
                    toggleTarifSup={toggleTarifSup}
                    modesPaiement={modesPaiement}
                    handleInputChange={handleInputChange}
                    calculerMontantActuel={calculerMontantActuel}
                    montantVerse={montantVerse}
                    handleMontantVerseChange={handleMontantVerseChange}
                    renderEcheances={renderEcheances}
                    documents={documents}
                    handleDocumentUpload={handleDocumentUpload}
                    handleDocumentRemove={handleDocumentRemove}
                  />
                )}
              </div>

              <NavigationButtons
                currentStep={currentStep}
                prevStep={prevStep}
                nextStep={nextStep}
                loading={loading}
                handleSubmit={handleSubmit}
              />
            </form>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default CreateInscriptionPage;